import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/i18n";
import { GraduationCap, Users, UserRound, Clock, MapPin, BookOpen, Layers, BarChart3 } from "lucide-react";

const mesCorto = (m, lang) => new Date(Number(m.slice(0, 4)), Number(m.slice(5, 7)) - 1, 1)
  .toLocaleDateString(lang === "en" ? "en-US" : "es-CR", { month: "short", year: "2-digit" });

const Spark = ({ values, color }) => {
  const w = 260;
  const h = 54;
  if (!values?.length) return null;
  const lo = Math.min(...values);
  const hi = Math.max(...values);
  const rango = hi - lo || 1;
  const pts = values.map((v, i) => [
    (w * i) / Math.max(values.length - 1, 1),
    h - 6 - ((v - lo) / rango) * (h - 16),
  ]);
  const d = `M${pts.map(([x, y]) => `${x.toFixed(1)} ${y.toFixed(1)}`).join(" L")}`;
  const ultimo = pts[pts.length - 1];
  return (
    <svg viewBox={`0 0 ${w} ${h}`} width="100%" height={h} preserveAspectRatio="none" className="block">
      <path d={`${d} L${w} ${h} L0 ${h}Z`} fill={color} opacity="0.1" />
      <path d={d} fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round"
            vectorEffect="non-scaling-stroke" />
      <circle cx={ultimo[0] - 2} cy={ultimo[1]} r="3.2" fill={color} />
    </svg>
  );
};

const Delta = ({ actual, anterior, sufijo = "" }) => {
  const { t } = useI18n();
  const dif = Math.round((actual - anterior) * 10) / 10;
  if (!anterior && !actual) return <span className="text-slate-400">{t("trend_no_data")}</span>;
  if (dif === 0) return <span><span className="font-bold text-slate-400">=</span> {t("trend_same_as_prev")}</span>;
  const arriba = dif > 0;
  return (
    <span>
      <span className={arriba ? "font-bold text-emerald-600" : "font-bold text-rose-600"}>
        {arriba ? "▲" : "▼"} {Math.abs(dif)}{sufijo}
      </span>{" "}
      {t("trend_vs_prev")}
    </span>
  );
};

const Grande = ({ label, icon: Icon, color, value, unidad, extra, delta, spark, testId }) => (
  <div className="bg-white border border-slate-200 rounded-2xl pt-4 px-4 pb-0 overflow-hidden
                  transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_14px_34px_-22px_rgba(15,23,42,0.4)]"
       data-testid={testId}>
    <div className="flex items-center justify-between">
      <span className="text-[13px] font-medium text-slate-500">{label}</span>
      <span className="h-7 w-7 rounded-lg grid place-items-center"
            style={{ background: `${color}1a`, color }}>
        <Icon className="h-3.5 w-3.5" />
      </span>
    </div>
    <div className="mt-2 text-[34px] font-extrabold tracking-[-0.035em] leading-none tabular-nums">
      {value}
      {unidad && <span className="ml-1 text-sm font-bold text-slate-400">{unidad}</span>}
      {extra && <span className="ml-1.5 text-sm font-bold text-slate-400">{extra}</span>}
    </div>
    <div className="mt-1.5 text-[11.5px] text-slate-500">{delta}</div>
    <div className="-mx-4 mt-1">{spark}</div>
  </div>
);

const Chico = ({ label, icon: Icon, color, value, unidad, testId }) => (
  <div className="bg-white border border-slate-200 rounded-xl px-3.5 py-3 flex items-center gap-2.5"
       data-testid={testId}>
    <span className="h-[29px] w-[29px] shrink-0 rounded-lg grid place-items-center"
          style={{ background: `${color}1a`, color }}>
      <Icon className="h-4 w-4" />
    </span>
    <div>
      <div className="text-xl font-extrabold tracking-[-0.02em] leading-none tabular-nums">
        {value}
        {unidad && <span className="ml-1 text-[11px] font-bold text-slate-400">{unidad}</span>}
      </div>
      <div className="mt-0.5 text-[12px] font-medium text-slate-500">{label}</div>
    </div>
  </div>
);

const pct = (parte, total) => (total ? `${Math.round((parte / total) * 100)}%` : null);
const miles = (n) => Number(n ?? 0).toLocaleString("es-CR");

export const SchoolTrends = () => {
  const { t, lang } = useI18n();
  const [d, setD] = useState(null);

  useEffect(() => {
    api.get("/stats/school-trends?months=12").then((r) => setD(r.data)).catch(() => setD(null));
  }, []);

  if (!d) {
    return (
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="school-trends-loading">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="h-[150px] bg-white border border-slate-200 rounded-2xl animate-pulse" />
        ))}
      </div>
    );
  }

  const s = d.series || [];
  const cur = d.current || {};
  const prev = d.previous || {};
  const mesActual = cur.month ? mesCorto(cur.month, lang) : "";

  return (
    <div data-testid="school-trends">
      <p className="mb-2.5 text-[13px] font-medium text-slate-500">
        {t("trend_important")}
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Grande
          testId="trend-card-students"
          label={t("trend_active_students")} icon={GraduationCap} color="#2563eb"
          value={miles(d.students_registry)}
          delta={
            <span>
              <span className="font-semibold text-slate-600">{miles(cur.students)}</span>{" "}
              {t("trend_with_enrollment")} · <Delta actual={cur.students} anterior={prev.students} />
            </span>
          }
          spark={<Spark values={s.map((x) => x.students)} color="#2563eb" />}
        />
        <Grande
          testId="trend-card-group"
          label={t("trend_group_students")} icon={Users} color="#7c3aed"
          value={miles(cur.group_students)} extra={pct(cur.group_students, cur.students)}
          delta={<Delta actual={cur.group_students} anterior={prev.group_students} />}
          spark={<Spark values={s.map((x) => x.group_students)} color="#7c3aed" />}
        />
        <Grande
          testId="trend-card-private"
          label={t("trend_private_students")} icon={UserRound} color="#0ea5a4"
          value={miles(cur.private_students)} extra={pct(cur.private_students, cur.students)}
          delta={<Delta actual={cur.private_students} anterior={prev.private_students} />}
          spark={<Spark values={s.map((x) => x.private_students)} color="#0ea5a4" />}
        />
        <Grande
          testId="trend-card-hours"
          label={`${t("trend_hours_month")} · ${mesActual}`} icon={Clock} color="#ea580c"
          value={miles(cur.hours)} unidad="h"
          delta={<Delta actual={cur.hours} anterior={prev.hours} sufijo=" h" />}
          spark={<Spark values={s.map((x) => x.hours)} color="#ea580c" />}
        />
      </div>

      <p className="mt-5 mb-2.5 text-[13px] font-medium text-slate-500">
        {t("trend_rest")}
      </p>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <Chico testId="trend-card-teachers" label={t("teachers")} icon={Users} color="#0f172a" value={miles(d.teachers)} />
        <Chico testId="trend-card-sedes" label={t("sedes")} icon={MapPin} color="#f97316" value={miles(d.sedes)} />
        <Chico testId="trend-card-programs" label={t("programs")} icon={BookOpen} color="#059669" value={miles(d.programs)} />
        <Chico testId="trend-card-groups" label={t("groups")} icon={Layers} color="#2563eb" value={miles(d.groups)} />
        <Chico testId="trend-card-hours-total" label={t("trend_hours_all_time")} icon={BarChart3} color="#be185d"
               value={miles(d.hours_all_time)} unidad="h" />
      </div>
    </div>
  );
};

export default SchoolTrends;
